/**
 * Editor Bridge — injected into customer site iframes by the DCS visual editor.
 *
 * Responsibilities:
 * 1. Report available sections and text keys to the parent portal
 * 2. Enable inline contenteditable editing on data-text-key/data-dcs-text elements (double-click)
 * 3. Render section overlay highlights on hover/select from parent
 * 4. Communicate all interactions back to the parent via postMessage
 * 5. Monitor navigation within the iframe and notify the parent
 * 6. Show floating edit icon on hover for text-key elements
 * 7. Show floating image icon on hover for images within sections
 *
 * Supports both legacy `data-text-key` and modern `data-dcs-text` attributes
 * for backward compatibility during the migration period.
 *
 * NOTE: AI ✨ buttons are rendered by the portal-side SectionOverlayLayer,
 * NOT by this bridge. The bridge only reports section/text key data and
 * handles inline text editing.
 *
 * This script runs inside the iframe (customer site). The parent portal
 * communicates via postMessage using the `dcs:*` message protocol.
 */

// ─── Types ───────────────────────────────────────────────────────

export interface SectionInfo {
  id: string
  label: string | null
  bounds: { x: number; y: number; width: number; height: number }
  textKeyCount: number
}

export interface EditorReadyPayload {
  sections: SectionInfo[]
  textKeys: string[]
  contentHeight: number
  /** Whether the page has a [data-blog-content] element (blog post page) */
  hasBlogContent: boolean
  /** Current innerHTML of the blog content element, if present */
  blogContentHtml: string | null
  /** Whether the page has a [data-event-content] or [data-rich-content="event"] element */
  hasEventContent: boolean
  /** Current innerHTML of the event content element, if present */
  eventContentHtml: string | null
  /** Current blog post title text from [data-blog-title], if present */
  blogTitle: string | null
  /** Current blog post summary/description text from [data-blog-summary], if present */
  blogSummary: string | null
  /** Whether the page has any managed form roots */
  hasManagedForms: boolean
  /** Form IDs for all detected managed-form roots */
  managedFormIds: string[]
  /** Whether the page has any [data-dcs-reviews] elements */
  hasReviews: boolean
  /** Keys of all [data-dcs-reviews] elements found on the page */
  reviewKeys: string[]
}

// ─── Message Protocol ────────────────────────────────────────────

type InboundMessageType =
  | 'dcs:init-editor'
  | 'dcs:highlight-section'
  | 'dcs:clear-highlight'
  | 'dcs:select-text-key'
  | 'dcs:update-text'
  | 'dcs:set-mode'
  | 'dcs:set-editing-enabled'
  | 'dcs:update-blog-content'
  | 'dcs:update-reviews'
  | 'dcs:image-set'

type OutboundMessageType =
  | 'dcs:ready'
  | 'dcs:text-key-click'
  | 'dcs:text-key-changed'
  | 'dcs:section-click'
  | 'dcs:section-hover'
  | 'dcs:text-key-dblclick'
  | 'dcs:contextmenu'
  | 'dcs:click'
  | 'dcs:navigation'
  | 'dcs:array-key-click'
  | 'dcs:image-click'
  | 'dcs:blog-content-click'
  | 'dcs:blog-content-ready'
  | 'dcs:blog-metadata-click'
  | 'dcs:managed-form-click'
  | 'dcs:reviews-click'

function isInIframe(): boolean {
  try { return globalThis.self !== globalThis.self.parent } catch { return true }
}

function postToParent(type: OutboundMessageType, data: unknown) {
  if (!isInIframe()) return
  // Use '*' because the portal origin varies between dev and production
  // and this script doesn't know the parent origin at injection time.
  // Security: messages are validated by the portal's useIframeBridge.
  // Origin validated via dcs: prefix check on all messages
  globalThis.self.parent.postMessage({ type, data }, '*') // NOSONAR
}

// ─── Attribute Compatibility ─────────────────────────────────────

/**
 * CSS selector that matches elements with either the legacy `data-text-key`
 * attribute or the modern `data-dcs-text` attribute. This ensures the editor
 * bridge works with both pre-migration and post-migration sites.
 */
const TEXT_KEY_SELECTOR = '[data-text-key], [data-dcs-text]'
const REVIEW_SELECTOR = '[data-dcs-reviews]'
const BLOG_CONTENT_SELECTOR = '[data-blog-content]'
const EVENT_CONTENT_SELECTOR = '[data-event-content], [data-rich-content="event"]'

/**
 * Get the text key value from an element, checking the modern `data-dcs-text`
 * attribute first, then falling back to the legacy `data-text-key`.
 */
function getTextKey(el: HTMLElement): string {
  return el.dataset.dcsText ?? el.dataset.textKey ?? ''
}

/**
 * Build a CSS selector that matches a specific text key value
 * across both legacy and modern attribute names.
 */
function textKeySelector(key: string): string {
  return `[data-text-key="${key}"], [data-dcs-text="${key}"]`
}

// ─── State ───────────────────────────────────────────────────────

let editorActive = false
let bridgeInitialized = false
let activeEditElement: HTMLElement | null = null
/** The current pathname tracked for navigation detection */
let lastKnownPathname: string = ''
/** The floating edit icon element shown on hover over text keys */
let editIconElement: HTMLElement | null = null
/** The element the edit icon is currently attached to */
let editIconTarget: HTMLElement | null = null
/** The floating edit icon for array/list text keys */
let arrayEditIconElement: HTMLElement | null = null
/** The element the array edit icon is currently attached to */
let arrayEditIconTarget: HTMLElement | null = null
/** The floating edit icons for image elements within sections (supports multiple for overlapping images) */
let imageEditIconElements: HTMLElement[] = []
/** The image elements the image edit icons are currently positioned on */
let imageEditIconTargets: HTMLImageElement[] = []
/** The floating edit icon for review sections */
let reviewEditIconElement: HTMLElement | null = null
/** The review section element the review edit icon is currently attached to */
let reviewEditIconTarget: HTMLElement | null = null
/** The floating manage-form button for managed form roots */
let managedFormEditIconElement: HTMLElement | null = null
/** The managed form root the floating button is currently attached to */
let managedFormEditIconTarget: HTMLElement | null = null
/** Whether editing features are enabled (disabled on unmanaged pages) */
let editingEnabled = true
/** Original text values captured when inline editing starts, for change detection */
const originalTextValues = new Map<HTMLElement, string>()
/** ResizeObserver for tracking section layout changes */
let sectionResizeObserver: ResizeObserver | null = null
/** Debounce timer for re-discovery triggered by layout changes */
let rediscoveryTimer: ReturnType<typeof setTimeout> | null = null
/** Track elements that have had editor event listeners attached (prevents duplicate listeners) */
const initializedTextElements = new WeakSet<HTMLElement>()
const initializedSections = new WeakSet<HTMLElement>()
const initializedReviewElements = new WeakSet<HTMLElement>()
const initializedManagedFormElements = new WeakSet<HTMLElement>()
let blogContentInitialized = false
let eventContentInitialized = false
let blogMetadataInitialized = false

/** Debounced re-discovery — collapses rapid layout changes into one update */
function scheduleRediscovery() {
  if (rediscoveryTimer) clearTimeout(rediscoveryTimer)
  rediscoveryTimer = setTimeout(() => {
    rediscoveryTimer = null
    rediscoverAndNotify()
  }, 300)
}

// ─── Section Discovery ───────────────────────────────────────────

function discoverSections(): SectionInfo[] {
  const elements = document.querySelectorAll<HTMLElement>('[data-section]')
  return Array.from(elements).map((el) => {
    const rect = el.getBoundingClientRect()
    const textKeyCount = el.querySelectorAll(TEXT_KEY_SELECTOR).length
    return {
      id: el.dataset.section!,
      label: el.dataset.sectionLabel ?? null,
      bounds: {
        x: rect.left + scrollX,
        y: rect.top + scrollY,
        width: rect.width,
        height: rect.height,
      },
      textKeyCount,
    }
  })
}

function discoverTextKeys(): string[] {
  return Array.from(document.querySelectorAll<HTMLElement>(TEXT_KEY_SELECTOR)).map(
    (el) => getTextKey(el),
  )
}

function discoverReviewKeys(): string[] {
  return Array.from(document.querySelectorAll<HTMLElement>(REVIEW_SELECTOR))
    .map(el => el.dataset.dcsReviews ?? '')
    .filter(Boolean)
}

function discoverManagedFormIds(): string[] {
  return Array.from(document.querySelectorAll<HTMLElement>('[data-form-key]'))
    .map(el => el.dataset.formKey ?? '')
    .filter(Boolean)
}

// ─── Layout Change Observation ───────────────────────────────────

/**
 * Watch for layout changes that affect section bounds and overall page height:
 * 1. ALL lazy-loaded images on the page — their load events shift subsequent sections
 * 2. ResizeObserver on the document body — detects overall page height changes
 * 3. ResizeObserver on section elements — detects any size change (font loading,
 *    dynamic content expansion, CSS transitions, etc.)
 *
 * When changes are detected, we schedule a debounced re-discovery so the portal
 * overlay layer receives updated section bounds and content height that match
 * the live layout.
 */
function observeSectionLayout() {
  // Clean up any previous observer (e.g., after SPA navigation)
  if (sectionResizeObserver) {
    sectionResizeObserver.disconnect()
  }

  // 1. Watch ALL lazy images on the page — not just inside sections.
  //    Images outside sections (e.g., contact form, unmapped content) can still
  //    push content down and change overall page height and section positions.
  const allImages = document.querySelectorAll<HTMLImageElement>('img')
  allImages.forEach(img => {
    if (!img.complete) {
      img.addEventListener('load', scheduleRediscovery, { once: true })
      img.addEventListener('error', scheduleRediscovery, { once: true })
    }
  })

  // 2. ResizeObserver on the document body + each section element.
  //    Body observation catches overall page height changes from any source.
  if (typeof ResizeObserver !== 'undefined') {
    sectionResizeObserver = new ResizeObserver(scheduleRediscovery)
    // Observe body for overall height changes
    sectionResizeObserver.observe(document.body)
    // Observe each section for individual bound changes
    document.querySelectorAll<HTMLElement>('[data-section]').forEach(el => {
      sectionResizeObserver!.observe(el)
    })
  }
}

// ─── Section Overlay ─────────────────────────────────────────────

/**
 * Section highlight is now handled entirely by the portal-side
 * SectionOverlayLayer rendered on top of the iframe. The bridge
 * only reports section bounds and hover events — no visual
 * overlays are rendered inside the iframe itself.
 */
function showSectionHighlight(_sectionId: string) {
  // No-op: portal overlay layer handles section highlights
}

function clearSectionHighlight() {
  // No-op: portal overlay layer handles section highlights
}

// ─── Navigation Monitoring ───────────────────────────────────────

/**
 * Monitor navigation within the iframe and notify the parent portal.
 * Instead of blocking all navigation, we allow SPA routing and inform
 * the portal when the iframe navigates to a different page. The portal
 * then updates its own URL and loads editing context for the new page.
 *
 * Strategy:
 * 1. Poll location.pathname on a short interval — this reliably detects
 *    SPA navigation regardless of which router or framework is used,
 *    since frameworks may cache History API references before our script loads.
 * 2. Block external/cross-origin link clicks (actual page reloads).
 * 3. Block form submissions.
 * 4. Monitor popstate for back/forward navigation.
 * 5. After navigation, re-discover sections and report them to the portal.
 */
function monitorNavigation() {
  lastKnownPathname = globalThis.location.pathname

  // ── Poll for URL changes — reliable with any SPA router ──────
  // Frameworks like VitePress/Vue Router may cache history.pushState before
  // our bridge loads, so prototype overrides are unreliable. Polling
  // location.pathname directly is simple and always works.
  setInterval(() => {
    checkForNavigation()
  }, 250)

  // ── Catch back/forward navigation ────────────────────────────
  globalThis.addEventListener('popstate', () => {
    checkForNavigation()
  })

  // ── Block external link clicks (cross-origin / full page reloads) ──
  const handleLinkClick = (e: MouseEvent) => {
    const link = (e.target as HTMLElement).closest?.('a[href]') as HTMLAnchorElement | null
    if (!link) return

    const href = link.getAttribute('href')
    if (!href || href.startsWith('#') || href === 'javascript:void(0)') return

    // Check if this is an external link (different origin)
    try {
      const resolved = new URL(href, globalThis.location.href)
      if (resolved.origin !== globalThis.location.origin) {
        // Block external navigation — can't leave the iframe
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
      }
      // Same-origin links: let the SPA router handle them.
      // The History API override above will detect the navigation.
    } catch {
      // Malformed URL — block it
      e.preventDefault()
    }
  }

  // Register on window first to beat VitePress's capture-phase handler
  globalThis.addEventListener('click', handleLinkClick, true)
  document.addEventListener('click', handleLinkClick, true)

  // Intercept form submissions
  document.addEventListener('submit', (e: Event) => {
    e.preventDefault()
    e.stopPropagation()
  }, true)

  // Block actual page reloads (location.href = ..., etc.)
  window.addEventListener('beforeunload', (e: BeforeUnloadEvent) => {
    e.preventDefault()
  })
}

/**
 * Check if the iframe pathname has changed and notify the portal.
 * Also re-initializes the editor bridge for the new page.
 */
function checkForNavigation() {
  const currentPathname = globalThis.location.pathname
  if (currentPathname === lastKnownPathname) return

  lastKnownPathname = currentPathname

  // Notify parent about the navigation
  postToParent('dcs:navigation', { pathname: currentPathname })

  // Clean up current edit state
  if (activeEditElement) {
    finishEdit(activeEditElement)
  }
  removeEditIcon()
  removeArrayEditIcon()
  removeReviewEditIcon()

  // Reset blog initialization flags so new page elements get listeners
  blogContentInitialized = false
  eventContentInitialized = false
  blogMetadataInitialized = false

  // Wait for the SPA router to finish rendering the new page,
  // then re-discover sections and text keys
  setTimeout(() => {
    rediscoverAndNotify()
  }, 500)
}

// ─── Mode Management ─────────────────────────────────────────────

/**
 * Set the current interaction mode. Both modes allow normal browsing —
 * inline editing works via double-click regardless of mode.
 * Switching to explore finishes any active inline edit.
 */
function setMode(mode: 'edit' | 'explore') {
  if (mode === 'explore') {
    // When explicitly switching to explore, finish any active inline edit
    if (activeEditElement) {
      finishEdit(activeEditElement)
    }
  }
}

// ─── Inline Text Editing ─────────────────────────────────────────

/**
 * Re-discover sections and text keys after an HMR update and
 * notify the parent portal so the overlay layer updates.
 */
function rediscoverAndNotify() {
  activeEditElement = null

  const sections = discoverSections()
  const textKeys = discoverTextKeys()
  const reviewKeys = discoverReviewKeys()
  const managedFormIds = discoverManagedFormIds()
  const contentHeight = measureContentHeight(sections)

  // Skip re-report if height hasn't meaningfully changed (< 2px drift)
  // AND sections/textKeys are the same count — avoids portal re-render churn.
  if (Math.abs(contentHeight - lastReportedHeight) <= 2) {
    // Height is stable — still report in case sections/textKeys changed
  }
  lastReportedHeight = contentHeight

  // Detect rich content elements
  const blogEl = document.querySelector<HTMLElement>(BLOG_CONTENT_SELECTOR)
  const hasBlogContent = !!blogEl
  const blogContentHtml = blogEl ? blogEl.innerHTML : null
  const eventEl = document.querySelector<HTMLElement>(EVENT_CONTENT_SELECTOR)
  const hasEventContent = !!eventEl
  const eventContentHtml = eventEl ? eventEl.innerHTML : null

  // Detect blog metadata elements
  const blogTitleEl = document.querySelector<HTMLElement>('[data-blog-title]')
  const blogSummaryEl = document.querySelector<HTMLElement>('[data-blog-summary]')
  const blogTitle = blogTitleEl ? blogTitleEl.textContent?.trim() ?? null : null
  const blogSummary = blogSummaryEl ? blogSummaryEl.textContent?.trim() ?? null : null

  // Always report — sections/textKeys may have changed even if height is stable
  postToParent('dcs:ready', {
    sections,
    textKeys,
    contentHeight,
    hasBlogContent,
    blogContentHtml,
    hasEventContent,
    eventContentHtml,
    blogTitle,
    blogSummary,
    hasManagedForms: managedFormIds.length > 0,
    managedFormIds,
    hasReviews: reviewKeys.length > 0,
    reviewKeys,
  } satisfies EditorReadyPayload)

  // Re-apply any not-yet-saved image swaps so an HMR/navigation re-render
  // doesn't revert the preview to the committed image.
  applyPendingImageOverrides()

  // Apply editor features to any new elements (e.g., after HMR or navigation)
  // without re-creating the ResizeObserver (which would cause an infinite loop)
  if (editorActive) {
    applyEditorToElements()

    // Observe any new section elements that appeared after navigation/HMR.
    // ResizeObserver.observe() is idempotent — safe to call on already-observed elements.
    if (sectionResizeObserver) {
      document.querySelectorAll<HTMLElement>('[data-section]').forEach(el => {
        sectionResizeObserver!.observe(el)
      })
    }
  }
}

function enableEditorMode() {
  if (editorActive) return
  editorActive = true

  // Inject editor styles
  injectEditorStyles()

  // Apply editor features (classes + listeners) to all current elements
  applyEditorToElements()
}

/**
 * Apply editor features (classes + event listeners) to elements on the page.
 * Uses WeakSets to track which elements have already been initialized,
 * preventing duplicate event listeners on repeated calls (e.g., after HMR
 * updates or layout-triggered re-discovery).
 */
function applyEditorToElements() {
  // Set up all text key elements — double-click to start editing
  const textElements = document.querySelectorAll<HTMLElement>(TEXT_KEY_SELECTOR)
  textElements.forEach((htmlEl) => {
    // Always ensure the class is present (may have been removed by set-editing-enabled)
    htmlEl.classList.add('dcs-editable')

    // Only add event listeners once per element
    if (initializedTextElements.has(htmlEl)) return
    initializedTextElements.add(htmlEl)

    // Double-click to start inline editing (signals parent to switch to edit mode)
    htmlEl.addEventListener('dblclick', handleTextKeyDblClick)
    // Blur to finish editing
    htmlEl.addEventListener('blur', handleTextKeyBlur)
    // Keyboard: Enter to commit, Escape to cancel (restores the pre-edit snapshot)
    htmlEl.addEventListener('keydown', handleTextKeyKeydown)
    // Paste: strip formatting on engines without contenteditable="plaintext-only"
    htmlEl.addEventListener('paste', handleTextKeyPaste)

    // Show/hide the floating edit icon on hover (T icon for regular text, List icon for array keys)
    htmlEl.addEventListener('mouseenter', () => {
      // Don't show icon while actively editing this or another element
      if (activeEditElement) return
      const key = getTextKey(htmlEl)
      if (isArrayTextKey(key)) {
        showArrayEditIcon(htmlEl)
      } else {
        showEditIcon(htmlEl)
      }
    })
    htmlEl.addEventListener('mouseleave', (e: MouseEvent) => {
      // Don't hide if moving to the edit icon or array edit icon
      const related = e.relatedTarget as HTMLElement | null
      if (related && (
        related.classList?.contains('dcs-edit-icon') || related.closest?.('.dcs-edit-icon') ||
        related.classList?.contains('dcs-array-edit-icon') || related.closest?.('.dcs-array-edit-icon')
      )) return
      removeEditIcon()
      removeArrayEditIcon()
    })
  })

  // Set up review sections — hover to show the floating reviews icon
  const reviewElements = document.querySelectorAll<HTMLElement>(REVIEW_SELECTOR)
  reviewElements.forEach((htmlEl) => {
    if (initializedReviewElements.has(htmlEl)) return
    initializedReviewElements.add(htmlEl)

    htmlEl.addEventListener('mouseenter', () => {
      // Don't show icon while actively editing text
      if (activeEditElement) return
      showReviewEditIcon(htmlEl)
    })
    htmlEl.addEventListener('mouseleave', (e: MouseEvent) => {
      // Don't hide if moving to the review edit icon
      const related = e.relatedTarget as HTMLElement | null
      if (related && (
        related.classList?.contains('dcs-review-edit-icon') || related.closest?.('.dcs-review-edit-icon')
      )) return
      removeReviewEditIcon()
    })
  })

  const managedFormElements = document.querySelectorAll<HTMLElement>('[data-form-key]')
  managedFormElements.forEach((formEl) => {
    formEl.classList.add('dcs-managed-form')

    if (!initializedManagedFormElements.has(formEl)) {
      initializedManagedFormElements.add(formEl)

      const openManagedForm = (e: Event) => {
        if (!editingEnabled) return
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        postToParent('dcs:managed-form-click', {
          formId: formEl.dataset.formKey ?? null,
        })
      }

      formEl.addEventListener('click', openManagedForm, true)
      formEl.addEventListener('dblclick', openManagedForm, true)
      formEl.addEventListener('mouseenter', () => {
        if (activeEditElement) return
        showManagedFormEditIcon(formEl)
      })
      formEl.addEventListener('mouseleave', (e: MouseEvent) => {
        const related = e.relatedTarget as HTMLElement | null
        if (related && (
          related.classList?.contains('dcs-managed-form-edit-icon') || related.closest?.('.dcs-managed-form-edit-icon')
        )) return
        removeManagedFormEditIcon()
      })
    }
  })

  // Set up section hover reporting (portal overlay handles the visual treatment)
  const sections = document.querySelectorAll<HTMLElement>('[data-section]')
  sections.forEach((el) => {
    if (initializedSections.has(el)) return
    initializedSections.add(el)

    const sectionId = el.dataset.section!

    el.addEventListener('mouseenter', () => {
      postToParent('dcs:section-hover', { sectionId })
    })
    el.addEventListener('mouseleave', () => {
      postToParent('dcs:section-hover', { sectionId: null })
      // Delayed cleanup of image icons — allows cursor to reach the icons
      setTimeout(() => {
        if (!isAnyImageIconHovered()) {
          removeAllImageEditIcons()
        }
      }, 100)
    })
    el.addEventListener('click', (e) => {
      // Only fire section click if not clicking on a text-key element
      const target = e.target as HTMLElement
      if (!target.closest(TEXT_KEY_SELECTOR)) {
        postToParent('dcs:section-click', { sectionId })
      }
    })

    // Image hover detection — detect when cursor is over an <img> inside a section.
    // Uses mousemove on the section because some images (e.g., inside sliders)
    // have pointer-events: none and cannot receive mouseenter directly.
    // For overlapping images (before/after sliders), show icons for ALL images
    // at the cursor position so each one is independently replaceable.
    el.addEventListener('mousemove', (e: MouseEvent) => {
      if (!editorActive || !editingEnabled || activeEditElement) {
        removeAllImageEditIcons()
        return
      }

      const allImages = findAllImagesAtPoint(el, e.clientX, e.clientY)
      if (allImages.length > 0) {
        showImageEditIcons(allImages)
      } else if (imageEditIconTargets.length > 0 && !isAnyImageIconHovered()) {
        removeAllImageEditIcons()
      }
    })
  })

  // Set up blog content area — click or double-click to open blog content editor
  if (!blogContentInitialized) {
    const blogContentEl = document.querySelector<HTMLElement>(BLOG_CONTENT_SELECTOR)
    if (blogContentEl) {
      blogContentInitialized = true
      blogContentEl.classList.add('dcs-blog-content')
      blogContentEl.addEventListener('click', (e) => {
        // Don't trigger blog content click if clicking on a text-key element
        const target = e.target as HTMLElement
        if (target.closest(TEXT_KEY_SELECTOR)) return
        e.preventDefault()
        e.stopPropagation()
        postToParent('dcs:blog-content-click', { textKey: 'blog-content' })
      })
      // Double-click / double-tap also opens the blog content editor
      blogContentEl.addEventListener('dblclick', (e) => {
        const target = e.target as HTMLElement
        if (target.closest(TEXT_KEY_SELECTOR)) return
        e.preventDefault()
        e.stopPropagation()
        postToParent('dcs:blog-content-click', { textKey: 'blog-content' })
      })
      // Also send the current content to the parent for initial load
      postToParent('dcs:blog-content-ready', { content: blogContentEl.innerHTML })
    }
  }

  // Set up event content area using the same rich-content editor protocol.
  if (!eventContentInitialized) {
    const eventContentEl = document.querySelector<HTMLElement>(EVENT_CONTENT_SELECTOR)
    if (eventContentEl) {
      eventContentInitialized = true
      eventContentEl.classList.add('dcs-blog-content')
      eventContentEl.addEventListener('click', (e) => {
        const target = e.target as HTMLElement
        if (target.closest(TEXT_KEY_SELECTOR)) return
        e.preventDefault()
        e.stopPropagation()
        postToParent('dcs:blog-content-click', { textKey: 'event-content' })
      })
      eventContentEl.addEventListener('dblclick', (e) => {
        const target = e.target as HTMLElement
        if (target.closest(TEXT_KEY_SELECTOR)) return
        e.preventDefault()
        e.stopPropagation()
        postToParent('dcs:blog-content-click', { textKey: 'event-content' })
      })
      postToParent('dcs:blog-content-ready', { content: eventContentEl.innerHTML })
    }
  }

  // Set up blog metadata elements — click to open blog metadata editor
  if (!blogMetadataInitialized) {
    const titleEl = document.querySelector<HTMLElement>('[data-blog-title]')
    const summaryEl = document.querySelector<HTMLElement>('[data-blog-summary]')
    if (titleEl || summaryEl) {
      blogMetadataInitialized = true
      const handler = (e: Event) => {
        e.preventDefault()
        e.stopPropagation()
        postToParent('dcs:blog-metadata-click', {
          title: titleEl?.textContent?.trim() ?? null,
          summary: summaryEl?.textContent?.trim() ?? null,
        })
      }
      titleEl?.addEventListener('click', handler)
      titleEl?.addEventListener('dblclick', handler)
      summaryEl?.addEventListener('click', handler)
      summaryEl?.addEventListener('dblclick', handler)
      // Add visual cue styles
      titleEl?.classList.add('dcs-blog-metadata')
      summaryEl?.classList.add('dcs-blog-metadata')
    }
  }

}

/**
 * Handle double-click on a text key element.
 * Notifies the parent portal to switch to edit mode and begins inline editing.
 */
function handleTextKeyDblClick(e: Event) {
  if (!editingEnabled) return

  e.preventDefault()
  e.stopPropagation()
  e.stopImmediatePropagation()

  const el = e.currentTarget as HTMLElement
  const key = getTextKey(el)
  const sectionParent = el.closest<HTMLElement>('[data-section]')
  const sectionId = sectionParent?.dataset.section ?? null

  // Always notify the parent to switch to edit mode when starting inline editing.
  // In explore mode this triggers the mode switch; in edit mode it's a no-op on the parent side.
  postToParent('dcs:text-key-dblclick', { key, sectionId })

  // Start inline editing
  startInlineEdit(el, key, sectionId)
}

/**
 * Feature-detect `contenteditable="plaintext-only"`.
 *
 * Baseline Newly Available 2025-03-04 (all three engines). It makes the *browser*
 * enforce the exact contract this bridge implements by hand: raw text editable,
 * rich formatting disabled, and pasted formatting stripped before it can ever
 * land in the customer site's live DOM.
 *
 * "Newly available" means older-but-still-in-use engines lack it, so the fallback
 * (`'true'` + {@link handleTextKeyPaste}) is not optional yet. Unsupporting engines
 * throw `SyntaxError` from the IDL setter; engines that neither throw nor implement
 * the value simply fail to reflect it onto the content attribute — so reflection is
 * the honest signal, and both misses land on the fallback.
 *
 * Deliberately NOT memoized: this runs once per user double-click, costs one
 * detached `<div>`, and a cached probe would need a test-only reset export.
 */
function supportsPlaintextOnly(): boolean {
  try {
    const probe = document.createElement('div')
    probe.contentEditable = 'plaintext-only'
    return probe.getAttribute('contenteditable') === 'plaintext-only'
  } catch {
    return false
  }
}

/**
 * Start inline contenteditable editing on a text key element.
 * Called by double-click or when the parent sends dcs:select-text-key.
 *
 * PLAIN-TEXT BOUNDARY (do not widen — see the contentEditable-simplification plan §3.2).
 * A keyed slot (`data-dcs-text` / legacy `data-text-key`) is a discrete, addressable,
 * plain STRING in the 3-layer content model (`useTextContent.ts`) and round-trips through
 * `.dcs/content.yaml`. Inline editing must therefore never produce markup:
 *   - `contenteditable` is `plaintext-only` where supported, `'true'` + a paste
 *     interceptor everywhere else;
 *   - the value is read back with `innerText` (never `innerHTML`);
 *   - `execCommand` is obsolete and is never called here;
 *   - rich bodies (blog/event) are a SEPARATE path handled by the portal-side
 *     managed editor — do not route them through this one.
 * Adding formatting/WYSIWYG here would erase slot boundaries and break the
 * `dcs:text-key-changed { key, value }` contract.
 */
function startInlineEdit(el: HTMLElement, key: string, sectionId: string | null) {
  // Finish any active edit first
  if (activeEditElement && activeEditElement !== el) {
    finishEdit(activeEditElement)
  }

  // Store original text for change detection on finish — and for Escape-to-cancel restore
  originalTextValues.set(el, el.innerText.trim())

  // Enable contenteditable. Prefer the browser-enforced plain-text mode; fall back
  // to 'true' (+ handleTextKeyPaste) on pre-Baseline engines. finishEdit/cancelInlineEdit
  // both guard on hasAttribute(), so they are value-agnostic.
  el.setAttribute('contenteditable', supportsPlaintextOnly() ? 'plaintext-only' : 'true')
  el.classList.add('dcs-editing')
  el.focus()
  activeEditElement = el

  // Select all text for easy replacement
  const selection = getSelection()
  const range = document.createRange()
  range.selectNodeContents(el)
  selection?.removeAllRanges()
  selection?.addRange(range)

  postToParent('dcs:text-key-click', { key, sectionId })
}

function handleTextKeyBlur(e: Event) {
  const el = e.currentTarget as HTMLElement
  finishEdit(el)
}

function handleTextKeyKeydown(e: KeyboardEvent) {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    ;(e.currentTarget as HTMLElement).blur()
  }
  if (e.key === 'Escape') {
    e.preventDefault()
    cancelInlineEdit(e.currentTarget as HTMLElement)
  }
}

/**
 * Paste interception — FALLBACK ONLY, for engines without
 * `contenteditable="plaintext-only"` (see {@link supportsPlaintextOnly}).
 *
 * Without it, a rich-HTML paste briefly injects foreign markup into the customer
 * site's live DOM and is only flattened later by the `innerText` read-back in
 * finishEdit. Here we never let the markup in at all.
 */
function handleTextKeyPaste(e: ClipboardEvent) {
  const el = e.currentTarget as HTMLElement
  // Only intercept the live inline edit; ignore any other paste on the page.
  if (el !== activeEditElement) return
  // Where the browser enforces plain text natively, stay out of the way so its
  // own undo stack / IME behaviour is untouched.
  if (el.getAttribute('contenteditable') === 'plaintext-only') return

  const text = e.clipboardData?.getData('text/plain') ?? ''
  e.preventDefault()
  insertPlainTextAtCaret(el, text)
}

/** Replace the current selection inside `el` with `text` as a bare text node. */
function insertPlainTextAtCaret(el: HTMLElement, text: string) {
  const selection = getSelection()
  if (!selection || selection.rangeCount === 0) {
    el.appendChild(document.createTextNode(text))
    return
  }

  const range = selection.getRangeAt(0)
  // Never write outside the element being edited.
  if (!el.contains(range.commonAncestorContainer)) return

  range.deleteContents()
  if (text) {
    const node = document.createTextNode(text)
    range.insertNode(node)
    range.setStartAfter(node)
  }
  range.collapse(true)
  selection.removeAllRanges()
  selection.addRange(range)
}

/**
 * Cancel an in-flight inline edit: restore the snapshot `startInlineEdit` captured
 * and leave edit mode WITHOUT reporting the abandoned value to the portal.
 *
 * Previously Escape just called `blur()`, and blur runs `finishEdit`, which COMMITS —
 * so "cancel" silently saved the thing the user was trying to discard.
 *
 * SKEW-SAFE BY CONSTRUCTION (Q-309=A / plan §5A). The bridge ships inside every
 * customer site's bundle while the portal deploys on its own train, so version skew
 * is permanent. Cancel therefore adds NO new `dcs:*` message and changes no payload:
 * it restores the DOM to the value the portal already believes is current and stays
 * silent on the wire. A new bridge against an older portal is indistinguishable from
 * "the user never edited anything"; an older bridge against a newer portal keeps its
 * old commit-on-Escape behaviour, which the (unchanged) portal already handles.
 */
function cancelInlineEdit(el: HTMLElement) {
  if (!el.hasAttribute('contenteditable')) return

  // Restore BEFORE finishing so finishEdit's diff compares restored-vs-snapshot
  // and posts nothing.
  const original = originalTextValues.get(el)
  if (original !== undefined) {
    el.innerText = original
  }

  el.blur()
  // blur() only fires (and only runs finishEdit) when the element actually had
  // focus; call it directly so cancel closes the edit either way. finishEdit
  // guards on the contenteditable attribute, so the second call is a no-op.
  finishEdit(el)
}

function finishEdit(el: HTMLElement) {
  if (!el.hasAttribute('contenteditable')) return

  el.removeAttribute('contenteditable')
  el.classList.remove('dcs-editing')

  const key = getTextKey(el)
  const sectionParent = el.closest<HTMLElement>('[data-section]')
  const sectionId = sectionParent?.dataset.section ?? null
  const newValue = el.innerText.trim()
  const originalValue = originalTextValues.get(el) ?? ''

  if (activeEditElement === el) {
    activeEditElement = null
  }

  // Only report to the portal when text was actually modified
  if (newValue !== originalValue) {
    postToParent('dcs:text-key-changed', { key, value: newValue, sectionId })
  }

  originalTextValues.delete(el)
}

function focusTextKey(key: string) {
  const el = document.querySelector<HTMLElement>(textKeySelector(key))
  if (!el) return
  el.scrollIntoView({ behavior: 'smooth', block: 'center' })

  const sectionParent = el.closest<HTMLElement>('[data-section]')
  const sectionId = sectionParent?.dataset.section ?? null

  // Start inline editing directly
  startInlineEdit(el, key, sectionId)
}

function updateTextInPlace(key: string, value: string) {
  const el = document.querySelector<HTMLElement>(textKeySelector(key))
  if (!el) return
  // Don't update if we're currently editing this element
  if (el === activeEditElement) return
  el.innerText = value
}

// ─── Edit Icon (Floating Text Cursor Button) ────────────────────

/**
 * Create the floating edit icon element. This small button appears
 * in the top-left corner of a hovered [data-text-key] or [data-dcs-text] element,
 * providing a click target to start inline editing without triggering
 * navigation (important for text inside buttons/links).
 */
function createEditIcon(): HTMLElement {
  const icon = document.createElement('button')
  icon.className = 'dcs-edit-icon'
  icon.setAttribute('type', 'button')
  icon.setAttribute('title', 'Edit text')
  icon.setAttribute('aria-label', 'Edit text inline')
  // SVG: Type icon (text cursor) — matches lucide "Type" icon
  icon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="4 7 4 4 20 4 20 7"/><line x1="9" y1="20" x2="15" y2="20"/><line x1="12" y1="4" x2="12" y2="20"/></svg>`

  icon.addEventListener('click', (e) => {
    e.preventDefault()
    e.stopPropagation()
    e.stopImmediatePropagation()

    if (!editIconTarget) return
    const el = editIconTarget
    const key = getTextKey(el)
    const sectionParent = el.closest<HTMLElement>('[data-section]')
    const sectionId = sectionParent?.dataset.section ?? null

    // Signal parent to switch to edit mode
    postToParent('dcs:text-key-dblclick', { key, sectionId })
    // Start inline editing
    startInlineEdit(el, key, sectionId)
    // Hide the icon while editing
    removeEditIcon()
  }, true)

  // Hide icon when mouse leaves it (and isn't going to the target element)
  icon.addEventListener('mouseleave', (e: MouseEvent) => {
    const related = e.relatedTarget as HTMLElement | null
    if (related && editIconTarget && (related === editIconTarget || editIconTarget.contains(related))) return
    removeEditIcon()
  })

  return icon
}

/**
 * Show the edit icon positioned at the top-left corner of the given element.
 */
function showEditIcon(el: HTMLElement) {
  if (!editingEnabled) return

  if (!editIconElement) {
    editIconElement = createEditIcon()
    document.body.appendChild(editIconElement)
  }

  editIconTarget = el

  // Position relative to the element's bounding box
  const rect = el.getBoundingClientRect()
  editIconElement.style.top = `${rect.top + scrollY - 4}px`
  editIconElement.style.left = `${rect.left + scrollX - 4}px`
  editIconElement.style.display = 'flex'
}

/**
 * Hide and detach the edit icon.
 */
function removeEditIcon() {
  if (editIconElement) {
    editIconElement.style.display = 'none'
  }
  editIconTarget = null
}

// ─── Array Key Detection ─────────────────────────────────────────

/**
 * Check if a text key is part of an array pattern (e.g., "conditions.0.title").
 * Array text keys contain an indexed segment — either:
 * - Pure numeric: `baseKey.N.field` (e.g., `conditions.0.title`)
 * - Hyphenated index: `baseKey.prefix-N.field` (e.g., `steps.step-0.title`)
 */
function isArrayTextKey(key: string): boolean {
  // Match pure numeric segment (.0.) or hyphenated index (.prefix-0.)
  return /\.\d+\./.test(key) || /\.\w+-\d+\./.test(key)
}

/**
 * Extract the base array key from an array text key.
 * - `conditions.0.title` → `conditions`
 * - `steps.step-0.title` → `steps.step`
 */
function getArrayBaseKey(key: string): string | null {
  // Try hyphenated pattern first (more specific)
  const hyphenMatch = key.match(/^(.+?)-\d+\./)
  if (hyphenMatch) return hyphenMatch[1]
  // Fall back to pure numeric pattern
  const numMatch = key.match(/^(.+?)\.\d+\./)
  return numMatch ? numMatch[1] : null
}

// ─── Array Edit Icon (Floating List Button) ──────────────────────

/**
 * Create the floating array edit icon element. This small button appears
 * in the top-left corner of a hovered [data-text-key] or [data-dcs-text] element whose key
 * matches an array pattern. Clicking it sends dcs:array-key-click to the
 * portal to open the array editor sheet.
 */
function createArrayEditIcon(): HTMLElement {
  const icon = document.createElement('button')
  icon.className = 'dcs-array-edit-icon'
  icon.setAttribute('type', 'button')
  icon.setAttribute('title', 'Edit list items')
  icon.setAttribute('aria-label', 'Edit list items in panel')
  // SVG: List icon — matches lucide "List" icon
  icon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>`

  icon.addEventListener('click', (e) => {
    e.preventDefault()
    e.stopPropagation()
    e.stopImmediatePropagation()

    if (!arrayEditIconTarget) return
    const el = arrayEditIconTarget
    const key = getTextKey(el)
    const arrayKey = getArrayBaseKey(key)
    if (!arrayKey) return

    const sectionParent = el.closest<HTMLElement>('[data-section]')
    const sectionId = sectionParent?.dataset.section ?? null

    // Signal parent to open array editor sheet
    postToParent('dcs:array-key-click', { arrayKey, sectionId })
    // Hide the icon
    removeArrayEditIcon()
  }, true)

  // Hide icon when mouse leaves it (and isn't going to the target element)
  icon.addEventListener('mouseleave', (e: MouseEvent) => {
    const related = e.relatedTarget as HTMLElement | null
    if (related && arrayEditIconTarget && (related === arrayEditIconTarget || arrayEditIconTarget.contains(related))) return
    removeArrayEditIcon()
  })

  return icon
}

/**
 * Show the array edit icon positioned at the top-left corner of the given element.
 */
function showArrayEditIcon(el: HTMLElement) {
  if (!editingEnabled) return

  if (!arrayEditIconElement) {
    arrayEditIconElement = createArrayEditIcon()
    document.body.appendChild(arrayEditIconElement)
  }

  arrayEditIconTarget = el

  // Position relative to the element's bounding box
  const rect = el.getBoundingClientRect()
  arrayEditIconElement.style.top = `${rect.top + scrollY - 4}px`
  arrayEditIconElement.style.left = `${rect.left + scrollX - 4}px`
  arrayEditIconElement.style.display = 'flex'
}

/**
 * Hide and detach the array edit icon.
 */
function removeArrayEditIcon() {
  if (arrayEditIconElement) {
    arrayEditIconElement.style.display = 'none'
  }
  arrayEditIconTarget = null
}

// ─── Review Edit Icon (Floating Star Button) ──────────────────────

/**
 * Create the floating review edit icon element. This small button appears
 * in the top-right corner of a hovered [data-dcs-reviews] element and
 * opens the reviews editor in the parent portal.
 */
function createReviewEditIcon(): HTMLElement {
  const icon = document.createElement('button')
  icon.className = 'dcs-review-edit-icon'
  icon.setAttribute('type', 'button')
  icon.setAttribute('title', 'Edit Reviews')
  icon.setAttribute('aria-label', 'Edit reviews in panel')
  icon.textContent = '★'

  icon.addEventListener('click', (e) => {
    e.preventDefault()
    e.stopPropagation()
    e.stopImmediatePropagation()

    if (!reviewEditIconTarget) return
    const el = reviewEditIconTarget
    const key = el.dataset.dcsReviews ?? ''
    if (!key) return

    const sectionParent = el.closest<HTMLElement>('[data-section]')
    const sectionId = sectionParent?.dataset.section ?? null

    postToParent('dcs:reviews-click', { key, sectionId })
    removeReviewEditIcon()
  }, true)

  // Hide icon when mouse leaves it (and isn't going to the target element)
  icon.addEventListener('mouseleave', (e: MouseEvent) => {
    const related = e.relatedTarget as HTMLElement | null
    if (related && reviewEditIconTarget && (related === reviewEditIconTarget || reviewEditIconTarget.contains(related))) return
    removeReviewEditIcon()
  })

  return icon
}

/**
 * Show the review edit icon positioned at the top-right corner of the given element.
 */
function showReviewEditIcon(el: HTMLElement) {
  if (!editingEnabled) return

  if (!reviewEditIconElement) {
    reviewEditIconElement = createReviewEditIcon()
    document.body.appendChild(reviewEditIconElement)
  }

  reviewEditIconTarget = el

  const rect = el.getBoundingClientRect()
  reviewEditIconElement.style.top = `${rect.top + scrollY - 4}px`
  reviewEditIconElement.style.left = `${rect.right + scrollX - 28}px`
  reviewEditIconElement.style.display = 'flex'
}

/**
 * Hide and detach the review edit icon.
 */
function removeReviewEditIcon() {
  if (reviewEditIconElement) {
    reviewEditIconElement.style.display = 'none'
  }
  reviewEditIconTarget = null
}

function createManagedFormEditIcon(): HTMLElement {
  const icon = document.createElement('button')
  icon.className = 'dcs-managed-form-edit-icon'
  icon.setAttribute('type', 'button')
  icon.setAttribute('title', 'Manage form')
  icon.setAttribute('aria-label', 'Manage form fields')
  icon.innerHTML = `
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M3 6h18"/><path d="M7 12h10"/><path d="M10 18h4"/>
    </svg>
    <span class="dcs-managed-form-edit-label">Manage form</span>
  `

  icon.addEventListener('click', (e) => {
    e.preventDefault()
    e.stopPropagation()
    e.stopImmediatePropagation()

    if (!managedFormEditIconTarget) return
    postToParent('dcs:managed-form-click', {
      formId: managedFormEditIconTarget.dataset.formKey ?? null,
    })
    removeManagedFormEditIcon()
  }, true)

  icon.addEventListener('mouseleave', (e: MouseEvent) => {
    const related = e.relatedTarget as HTMLElement | null
    if (related && managedFormEditIconTarget && (related === managedFormEditIconTarget || managedFormEditIconTarget.contains(related))) return
    removeManagedFormEditIcon()
  })

  return icon
}

function showManagedFormEditIcon(el: HTMLElement) {
  if (!editingEnabled) return

  if (!managedFormEditIconElement) {
    managedFormEditIconElement = createManagedFormEditIcon()
    document.body.appendChild(managedFormEditIconElement)
  }

  managedFormEditIconTarget = el

  const rect = el.getBoundingClientRect()
  managedFormEditIconElement.style.top = `${rect.top + scrollY + 8}px`
  managedFormEditIconElement.style.left = `${rect.left + scrollX + 8}px`
  managedFormEditIconElement.style.display = 'flex'
}

function removeManagedFormEditIcon() {
  if (managedFormEditIconElement) {
    managedFormEditIconElement.style.display = 'none'
  }
  managedFormEditIconTarget = null
}

// ─── Image Edit Icon (Floating Camera Button) ───────────────────

/**
 * Create a floating image edit icon element for a specific image. This small
 * button appears in the top-right corner of a hovered image within a
 * [data-section] area, providing a click target to open image management —
 * essential for images inside interactive widgets (e.g., before/after sliders)
 * where right-click is intercepted by the widget.
 *
 * When multiple images overlap (e.g., before/after sliders), separate icons
 * are created for each image with labels to distinguish them.
 */
function createImageEditIcon(label?: string): HTMLElement {
  const icon = document.createElement('button')
  icon.className = 'dcs-image-edit-icon'
  icon.setAttribute('type', 'button')
  icon.setAttribute('title', label ? `Replace ${label}` : 'Manage image')
  icon.setAttribute('aria-label', label ? `Replace ${label}` : 'Manage image')
  // SVG: Image icon — matches lucide "Image" icon
  const svgHtml = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>`
  icon.innerHTML = label ? `${svgHtml}<span class="dcs-image-edit-label">${label}</span>` : svgHtml

  // Hide icon when mouse leaves it (and isn't going to the image)
  icon.addEventListener('mouseleave', (e: MouseEvent) => {
    const related = e.relatedTarget as HTMLElement | null
    // Keep icons visible if cursor moves back toward any tracked image
    if (related) {
      for (const target of imageEditIconTargets) {
        if (related === target || target.contains(related)) return
      }
      // Also keep visible if moving to another image icon
      if (related.classList?.contains('dcs-image-edit-icon') || related.closest?.('.dcs-image-edit-icon')) return
    }
    removeAllImageEditIcons()
  })

  return icon
}

function getManagedImageKey(img: HTMLImageElement): string | null {
  return img.dataset.dcsImageKey || img.closest<HTMLElement>('[data-dcs-image-key]')?.dataset.dcsImageKey || null
}

/**
 * The managed ALT key for an image (`data-dcs-image-alt-key`, e.g.
 * `hero.image.alt` under the D1-A imageBase contract, or the legacy `altKey`).
 * Null when the image has no managed alt key — the portal then must not
 * preview an alt change, because Save could not persist it.
 */
function getManagedImageAltKey(img: HTMLImageElement): string | null {
  return img.dataset.dcsImageAltKey || img.closest<HTMLElement>('[data-dcs-image-alt-key]')?.dataset.dcsImageAltKey || null
}

// ─── Live Image Swap (Phase 4 — dcs:image-set) ───────────────────

/**
 * Pending image overrides keyed by managed image key (`data-dcs-image-key`,
 * e.g. `hero.image.src`). The portal pushes these on "Replace without AI" so
 * the swap is visible in the preview BEFORE Save. They are re-applied after
 * every re-render (HMR / SPA navigation), mirroring how text overrides survive
 * a reload — the portal is the source of truth but the bridge replays its last
 * known value so a hot-reload doesn't revert the preview.
 */
const pendingImageOverrides = new Map<string, { src: string; alt?: string | null }>()

/**
 * Escape a managed key for use inside a quoted CSS attribute selector
 * (`[data-dcs-image-key="…"]`). Prefers the platform `CSS.escape`; falls back
 * to escaping backslashes and double-quotes — the only characters that can
 * break out of a double-quoted CSS string — in environments without it
 * (e.g. jsdom test runs, where the `CSS` global is absent).
 */
function cssEscapeAttributeValue(value: string): string {
  const cssGlobal = (globalThis as { CSS?: { escape?: (v: string) => string } }).CSS
  if (typeof cssGlobal?.escape === 'function') return cssGlobal.escape(value)
  return value.replace(/[\\"]/g, '\\$&')
}

/**
 * Swap the live `<img>`(s) carrying a managed image key to a new source.
 *
 * Mirrors the self-heal invariant: a `<picture>` does NOT re-resolve after its
 * `<source>` set changes, so the stale `<source>` siblings are removed and the
 * `srcset` on the `<img>` cleared — otherwise the browser keeps serving the old
 * variant. `data-dcs-image-url`/`-alt` are updated too so a subsequent
 * right-click reports the new image, not the replaced one.
 *
 * @returns the number of elements updated (0 when the key isn't on the page).
 */
export function applyImageOverride(key: string, src: string, alt?: string | null): number {
  if (!key) return 0
  // CSS-escape the key so quotes/backslashes/etc. can't break out of the
  // attribute selector or throw a SyntaxError that aborts the handler.
  const escapedKey = cssEscapeAttributeValue(key)

  const targets = new Set<HTMLImageElement>()
  // The key lives directly on the <img> (ManagedImage/ResponsiveImage) …
  document.querySelectorAll<HTMLImageElement>(`img[data-dcs-image-key="${escapedKey}"]`).forEach((img) => targets.add(img))
  // … or on a wrapper whose descendant <img> is the managed image.
  document.querySelectorAll<HTMLElement>(`[data-dcs-image-key="${escapedKey}"]`).forEach((el) => {
    if (el instanceof HTMLImageElement) {
      targets.add(el)
    } else {
      el.querySelectorAll<HTMLImageElement>('img').forEach((img) => targets.add(img))
    }
  })

  targets.forEach((img) => {
    // Drop the responsive <source> set + srcset so the new src wins immediately.
    img.closest('picture')?.querySelectorAll('source').forEach((s) => s.remove())
    img.removeAttribute('srcset')
    img.src = src
    img.dataset.dcsImageUrl = src
    if (alt != null) {
      img.alt = alt
      img.dataset.dcsImageAlt = alt
    }
  })

  return targets.size
}

/**
 * Record + apply an image override pushed from the portal.
 */
function setImageOverride(key: string, src: string, alt?: string | null): void {
  pendingImageOverrides.set(key, { src, alt })
  applyImageOverride(key, src, alt)
  // The new image may have a different intrinsic size — re-report bounds/height.
  scheduleRediscovery()
}

/**
 * Re-apply all pending image overrides. Called after a re-render (HMR / SPA nav)
 * so a hot reload doesn't revert a not-yet-saved swap.
 */
export function applyPendingImageOverrides(): void {
  for (const [key, override] of pendingImageOverrides) {
    applyImageOverride(key, override.src, override.alt)
  }
}

/**
 * Record a pending image override (test/utility seam for the replay path).
 */
export function recordPendingImageOverride(key: string, src: string, alt?: string | null): void {
  pendingImageOverrides.set(key, { src, alt })
}

/**
 * Handle a `dcs:update-text` that actually targets a MANAGED IMAGE key.
 *
 * The portal's discard/undo paths (discardTextChanges / discardSingleTextChange
 * in visualEditor.ts) revert EVERY staged key — including image keys — via the
 * text revert callback, which sends `dcs:update-text`. `updateTextInPlace` only
 * touches `data-text-key` elements, a no-op for image keys (which live on
 * `data-dcs-image-key`), while `pendingImageOverrides` would keep re-applying
 * the discarded src on every re-render — the discarded swap stayed sticky until
 * a manual refresh. So: if the key has a pending image override (or a managed
 * image element on the page), drop the sticky override and swap the live
 * `<img>` back to the reverted URL. Covers discard-all, discard-single, undo,
 * and reload-drops-staged-swap in one place.
 *
 * @returns true when the key was handled as an image revert (skip text handling).
 */
export function revertImageOverrideIfImageKey(key: string, value: string): boolean {
  if (!key) return false
  const isPending = pendingImageOverrides.has(key)
  const isImageElement =
    !isPending &&
    !!document.querySelector(`[data-dcs-image-key="${cssEscapeAttributeValue(key)}"]`)
  if (!isPending && !isImageElement) return false

  pendingImageOverrides.delete(key)
  if (value) applyImageOverride(key, value)
  return true
}

/**
 * Handle a `dcs:update-text` that targets a MANAGED IMAGE **ALT** key
 * (`data-dcs-image-alt-key`). The portal stages an alt change alongside the
 * src change on "Replace without AI"; on discard/undo it reverts each staged
 * key via `dcs:update-text`. Alt keys are attributes, not text nodes, so
 * `updateTextInPlace` would be a silent no-op — instead restore the `<img>`
 * alt and refresh any pending image override (keyed by the SRC key) so a
 * re-render replay does not resurrect the discarded alt.
 *
 * @returns true when the key was handled as an image-alt revert.
 */
export function revertImageAltOverrideIfAltKey(key: string, value: string): boolean {
  if (!key) return false
  const escapedKey = cssEscapeAttributeValue(key)
  const hosts = document.querySelectorAll<HTMLElement>(`[data-dcs-image-alt-key="${escapedKey}"]`)
  if (hosts.length === 0) return false

  hosts.forEach((host) => {
    const imgs = host instanceof HTMLImageElement
      ? [host]
      : Array.from(host.querySelectorAll('img'))
    imgs.forEach((img) => {
      img.alt = value
      img.dataset.dcsImageAlt = value
    })
    // Keep the replay map honest: the sticky override for this image's SRC key
    // must carry the reverted alt, not keep re-applying the discarded one.
    const srcKey = host.dataset.dcsImageKey
      || host.closest<HTMLElement>('[data-dcs-image-key]')?.dataset.dcsImageKey
    if (srcKey) {
      const pending = pendingImageOverrides.get(srcKey)
      if (pending) pendingImageOverrides.set(srcKey, { ...pending, alt: value })
    }
  })
  return true
}

/** Clear all pending image overrides (test isolation). */
export function clearPendingImageOverrides(): void {
  pendingImageOverrides.clear()
}

/**
 * Show image edit icons for all provided images. When only one image is
 * present, a single icon appears at the top-right corner. When multiple
 * overlapping images are present (e.g., before/after sliders), separate
 * labeled icons appear stacked in the top-right corner.
 */
function showImageEditIcons(images: HTMLImageElement[]) {
  if (!editingEnabled) return

  // Check if icons are already showing for these exact images
  if (imageEditIconTargets.length === images.length &&
      images.every((img, i) => imageEditIconTargets[i] === img)) return

  // Clear existing icons
  removeAllImageEditIcons()

  imageEditIconTargets = [...images]

  // Use the bounding box of the first image for positioning (they overlap anyway)
  const refRect = images[0].getBoundingClientRect()
  const hasMultiple = images.length > 1

  images.forEach((img, index) => {
    // Derive a label for multi-image scenarios from alt text
    let label: string | undefined
    if (hasMultiple) {
      const alt = img.alt ?? ''
      // Try to extract a meaningful label from alt text (e.g., "Project — Before" → "Before")
      const dashMatch = alt.match(/\u2014\s*(.+)$/) ?? alt.match(/-\s*(.+)$/)
      label = dashMatch ? dashMatch[1].trim() : `Image ${index + 1}`
    }

    const iconEl = createImageEditIcon(label)

    // Attach click handler with the specific image reference
    iconEl.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      e.stopImmediatePropagation()

      const sectionEl = img.closest<HTMLElement>('[data-section]')
      postToParent('dcs:image-click', {
        imageUrl: img.currentSrc ?? img.src,
        imageAlt: img.alt ?? null,
        imageWidth: img.naturalWidth,
        imageHeight: img.naturalHeight,
        imageKey: getManagedImageKey(img),
        imageAltKey: getManagedImageAltKey(img),
        sectionId: sectionEl?.dataset.section ?? null,
        sectionLabel: sectionEl?.dataset.sectionLabel ?? null,
      })
      removeAllImageEditIcons()
    }, true)

    document.body.appendChild(iconEl)

    // Stack icons vertically from the top-right corner
    iconEl.style.top = `${refRect.top + scrollY + 4 + (index * 28)}px`
    iconEl.style.left = hasMultiple
      ? `${refRect.right + scrollX - iconEl.offsetWidth - 4}px`
      : `${refRect.right + scrollX - 28}px`
    iconEl.style.display = 'flex'

    // Re-measure after display for labeled icons with dynamic width
    if (hasMultiple) {
      requestAnimationFrame(() => {
        iconEl.style.left = `${refRect.right + scrollX - iconEl.offsetWidth - 4}px`
      })
    }

    imageEditIconElements.push(iconEl)
  })
}

/**
 * Check if any image edit icon is currently being hovered.
 */
function isAnyImageIconHovered(): boolean {
  return imageEditIconElements.some(el => el.matches(':hover'))
}

/**
 * Hide and remove all image edit icons.
 */
function removeAllImageEditIcons() {
  for (const el of imageEditIconElements) {
    el.remove()
  }
  imageEditIconElements = []
  imageEditIconTargets = []
}

// ─── Shared Image Detection ──────────────────────────────────────

/**
 * Find ALL images at the given viewport coordinates within a section.
 * Returns images in DOM order (first = visually behind, last = on top).
 * Used for overlapping image scenarios like before/after sliders where
 * multiple <img> elements occupy the same bounding box area.
 */
function findAllImagesAtPoint(sectionEl: HTMLElement, clientX: number, clientY: number): HTMLImageElement[] {
  const results: HTMLImageElement[] = []
  const images = sectionEl.querySelectorAll<HTMLImageElement>('img')
  for (const img of images) {
    // Skip tiny images (icons, spacers, SVG fallbacks)
    if (img.naturalWidth > 0 && img.naturalWidth < 50 && img.naturalHeight < 50) continue
    const rect = img.getBoundingClientRect()
    if (rect.width < 30 || rect.height < 30) continue

    if (
      clientX >= rect.left && clientX <= rect.right &&
      clientY >= rect.top && clientY <= rect.bottom
    ) {
      results.push(img)
    }
  }
  return results
}

function injectEditorStyles() {
  if (document.getElementById('dcs-editor-styles')) return

  const style = document.createElement('style')
  style.id = 'dcs-editor-styles'
  style.textContent = `
    [data-text-key].dcs-editable,
    [data-dcs-text].dcs-editable {
      cursor: text !important;
      border-radius: 4px;
      position: relative;
    }

    [data-text-key].dcs-editable:hover,
    [data-dcs-text].dcs-editable:hover {
      outline: 2px dashed hsl(221.2 83.2% 53.3% / 0.4) !important;
      outline-offset: 4px;
      background-color: hsl(221.2 83.2% 53.3% / 0.03);
    }

    [data-text-key].dcs-editing,
    [data-dcs-text].dcs-editing {
      outline: 2px solid hsl(221.2 83.2% 53.3%) !important;
      outline-offset: 4px;
      background-color: hsl(221.2 83.2% 53.3% / 0.06);
      min-height: 1em;
    }

    [data-text-key].dcs-editing:focus,
    [data-dcs-text].dcs-editing:focus {
      outline: 2px solid hsl(221.2 83.2% 53.3%) !important;
      outline-offset: 4px;
    }

    .dcs-section-highlight {
      pointer-events: none;
    }

    /* Floating edit icon button */
    .dcs-edit-icon {
      position: absolute;
      z-index: 99999;
      display: none;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      padding: 0;
      margin: 0;
      border: 1.5px solid hsl(221.2 83.2% 53.3%);
      border-radius: 4px;
      background: hsl(221.2 83.2% 53.3% / 0.1);
      backdrop-filter: blur(4px);
      color: hsl(221.2 83.2% 53.3%);
      cursor: pointer;
      pointer-events: auto;
      transition: background 0.15s, transform 0.1s;
      box-shadow: 0 1px 3px rgba(0,0,0,0.12);
    }

    .dcs-edit-icon:hover {
      background: hsl(221.2 83.2% 53.3% / 0.25);
      transform: scale(1.1);
    }

    .dcs-edit-icon svg {
      display: block;
    }

    /* Floating array edit icon button */
    .dcs-array-edit-icon {
      position: absolute;
      z-index: 99999;
      display: none;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      padding: 0;
      margin: 0;
      border: 1.5px solid hsl(271 91% 65%);
      border-radius: 4px;
      background: hsl(271 91% 65% / 0.1);
      backdrop-filter: blur(4px);
      color: hsl(271 91% 65%);
      cursor: pointer;
      pointer-events: auto;
      transition: background 0.15s, transform 0.1s;
      box-shadow: 0 1px 3px rgba(0,0,0,0.12);
    }

    .dcs-array-edit-icon:hover {
      background: hsl(271 91% 65% / 0.25);
      transform: scale(1.1);
    }

    .dcs-array-edit-icon svg {
      display: block;
    }

    /* Floating review edit icon button */
    .dcs-review-edit-icon {
      position: absolute;
      z-index: 99999;
      display: none;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      padding: 0;
      margin: 0;
      border: 1.5px solid #f59e0b;
      border-radius: 4px;
      background: rgb(245 158 11 / 0.1);
      backdrop-filter: blur(4px);
      color: #f59e0b;
      cursor: pointer;
      pointer-events: auto;
      transition: background 0.15s, transform 0.1s;
      box-shadow: 0 1px 3px rgba(0,0,0,0.12);
      font-size: 14px;
      line-height: 1;
    }

    .dcs-review-edit-icon:hover {
      background: rgb(245 158 11 / 0.25);
      transform: scale(1.1);
    }

    .dcs-managed-form-edit-icon {
      position: absolute;
      z-index: 99999;
      display: none;
      align-items: center;
      justify-content: center;
      gap: 4px;
      min-width: 24px;
      height: 24px;
      padding: 0 8px;
      margin: 0;
      border: 1.5px solid hsl(271 91% 65%);
      border-radius: 4px;
      background: hsl(271 91% 65% / 0.12);
      backdrop-filter: blur(4px);
      color: hsl(271 91% 65%);
      cursor: pointer;
      pointer-events: auto;
      transition: background 0.15s, transform 0.1s;
      box-shadow: 0 1px 3px rgba(0,0,0,0.12);
      font-size: 11px;
      font-weight: 600;
      line-height: 1;
      white-space: nowrap;
    }

    .dcs-managed-form-edit-icon:hover {
      background: hsl(271 91% 65% / 0.24);
      transform: scale(1.03);
    }

    .dcs-managed-form-edit-icon svg {
      display: block;
      flex-shrink: 0;
    }

    .dcs-managed-form-edit-label {
      white-space: nowrap;
    }

    /* Floating image edit icon button — green to distinguish from text (blue) and array (violet) */
    .dcs-image-edit-icon {
      position: absolute;
      z-index: 99999;
      display: none;
      align-items: center;
      justify-content: center;
      min-width: 24px;
      height: 24px;
      padding: 0 4px;
      margin: 0;
      border: 1.5px solid hsl(142 71% 45%);
      border-radius: 4px;
      background: hsl(142 71% 45% / 0.1);
      backdrop-filter: blur(4px);
      color: hsl(142 71% 45%);
      cursor: pointer;
      pointer-events: auto;
      transition: background 0.15s, transform 0.1s;
      box-shadow: 0 1px 3px rgba(0,0,0,0.12);
      gap: 2px;
    }

    .dcs-image-edit-icon:hover {
      background: hsl(142 71% 45% / 0.25);
      transform: scale(1.1);
    }

    .dcs-image-edit-icon svg {
      display: block;
      flex-shrink: 0;
    }

    /* Label shown inside image edit icon for multi-image scenarios (e.g., before/after) */
    .dcs-image-edit-label {
      font-size: 9px;
      font-weight: 700;
      letter-spacing: 0.03em;
      white-space: nowrap;
      margin-left: 2px;
    }

    /* Blog content area — click to open WYSIWYG editor */
    [data-blog-content].dcs-blog-content {
      cursor: pointer;
      border-radius: 8px;
      position: relative;
      transition: outline-color 0.2s, background-color 0.2s;
    }

    [data-blog-content].dcs-blog-content:hover {
      outline: 2px dashed hsl(142 71% 45% / 0.5) !important;
      outline-offset: 8px;
      background-color: hsl(142 71% 45% / 0.03);
    }

    [data-blog-content].dcs-blog-content::after {
      content: 'Click to edit blog content';
      position: absolute;
      top: -28px;
      left: 8px;
      font-size: 11px;
      font-weight: 600;
      color: hsl(142 71% 45%);
      background: hsl(142 71% 45% / 0.1);
      border: 1px solid hsl(142 71% 45% / 0.3);
      padding: 2px 8px;
      border-radius: 4px;
      opacity: 0;
      transition: opacity 0.2s;
      pointer-events: none;
      white-space: nowrap;
    }

    [data-blog-content].dcs-blog-content:hover::after {
      opacity: 1;
    }

    /* Managed form area — click to open the portal Form Manager */
    [data-form-key].dcs-managed-form {
      cursor: pointer;
      border-radius: 12px;
      position: relative;
      transition: outline-color 0.2s, background-color 0.2s;
    }

    [data-form-key].dcs-managed-form:hover {
      outline: 2px dashed hsl(271 91% 65% / 0.65) !important;
      outline-offset: 4px;
      background-color: hsl(271 91% 65% / 0.04);
    }

    /* Blog metadata (title / summary) — click to edit */
    .dcs-blog-metadata {
      cursor: pointer;
      border-radius: 4px;
      transition: outline-color 0.2s, background-color 0.2s;
    }
    .dcs-blog-metadata:hover {
      outline: 2px dashed hsl(217 91% 60% / 0.5) !important;
      outline-offset: 4px;
      background-color: hsl(217 91% 60% / 0.04);
    }
  `
  document.head.appendChild(style)
}

// ─── Inbound Message Handler ─────────────────────────────────────

function handleMessage(event: MessageEvent) {
  // Validate message structure (origin is not checked because
  // the parent portal origin varies between dev and production)
  const msg = event.data
  if (!msg || typeof msg !== 'object' || typeof msg.type !== 'string') return
  if (!msg.type.startsWith('dcs:')) return

  const { type, data } = msg as { type: InboundMessageType; data: unknown }

  switch (type) {
    case 'dcs:init-editor':
      enableEditorMode()
      break

    case 'dcs:highlight-section':
      if (data && typeof data === 'object' && 'sectionId' in data) {
        showSectionHighlight((data as { sectionId: string }).sectionId)
      }
      break

    case 'dcs:clear-highlight':
      clearSectionHighlight()
      break

    case 'dcs:select-text-key':
      if (data && typeof data === 'object' && 'key' in data) {
        focusTextKey((data as { key: string }).key)
      }
      break

    case 'dcs:update-text':
      if (data && typeof data === 'object' && 'key' in data && 'value' in data) {
        const d = data as { key: string; value: string }
        // Discard/undo reuses this message for IMAGE keys too — revert the
        // live swap (and drop the sticky pending override) instead of the
        // text path, which is a no-op for data-dcs-image-key elements.
        // Image ALT keys likewise revert the <img> alt attribute in place.
        if (!revertImageOverrideIfImageKey(d.key, d.value)
          && !revertImageAltOverrideIfAltKey(d.key, d.value)) {
          updateTextInPlace(d.key, d.value)
        }
      }
      break

    case 'dcs:set-mode':
      if (data && typeof data === 'object' && 'mode' in data) {
        setMode((data as { mode: 'edit' | 'explore' }).mode)
      }
      break

    case 'dcs:set-editing-enabled':
      if (data && typeof data === 'object' && 'enabled' in data) {
        editingEnabled = (data as { enabled: boolean }).enabled
        if (editingEnabled) {
          // Re-add dcs-editable class to text key elements
          document.querySelectorAll<HTMLElement>(TEXT_KEY_SELECTOR).forEach(el => {
            el.classList.add('dcs-editable')
          })
        } else {
          // Clean up: finish any active edit, remove edit icon, remove hover styles
          if (activeEditElement) {
            finishEdit(activeEditElement)
          }
          removeEditIcon()
          removeArrayEditIcon()
          removeReviewEditIcon()
          removeManagedFormEditIcon()
          removeAllImageEditIcons()
          // Remove dcs-editable class to disable hover outlines
          document.querySelectorAll<HTMLElement>('.dcs-editable').forEach(el => {
            el.classList.remove('dcs-editable')
          })
        }
      }
      break

    case 'dcs:update-blog-content':
      if (data && typeof data === 'object' && 'content' in data) {
        const contentEl = document.querySelector<HTMLElement>(BLOG_CONTENT_SELECTOR)
          ?? document.querySelector<HTMLElement>(EVENT_CONTENT_SELECTOR)
        if (contentEl) {
          contentEl.innerHTML = (data as { content: string }).content
          // Trigger re-discovery so the portal overlay updates for any height changes
          scheduleRediscovery()
        }
      }
      break

    case 'dcs:image-set':
      if (data && typeof data === 'object' && 'key' in data && 'src' in data) {
        const d = data as { key: string; src: string; alt?: string | null }
        if (typeof d.key === 'string' && typeof d.src === 'string') {
          setImageOverride(d.key, d.src, d.alt ?? null)
        }
      }
      break

    case 'dcs:update-reviews':
      if (data && typeof data === 'object' && 'key' in data) {
        const d = data as { key: string; reviews?: unknown }
        globalThis.dispatchEvent(new CustomEvent('dcs:reviews-updated', {
          detail: {
            key: d.key,
            reviews: Array.isArray(d.reviews) ? d.reviews : null,
          },
        }))
        scheduleRediscovery()
      }
      break
  }
}

/**
 * Inject a stylesheet that neutralizes viewport-relative min-heights.
 * In the visual editor iframe, the portal controls height from body.scrollHeight.
 * CSS rules like `min-height: 100vh` create a feedback loop:
 *   iframe height → vh grows → body.scrollHeight grows → iframe height grows → …
 * Neutralizing these rules lets the content determine its natural height.
 */
function injectEditorHeightFix() {
  if (document.getElementById('dcs-editor-height-fix')) return
  const style = document.createElement('style')
  style.id = 'dcs-editor-height-fix'
  style.textContent = `
    /* DCS Editor Bridge: Neutralize viewport-relative min-heights */
    .min-h-screen, .min-h-dvh, .min-h-svh,
    .min-h-\\[100vh\\], .min-h-\\[100dvh\\], .min-h-\\[100svh\\] {
      min-height: 0 !important;
    }
  `
  document.head.appendChild(style)
}

/** Last reported content height — used to avoid re-sending when stable */
let lastReportedHeight = 0

/**
 * Measure the true content height, avoiding inflation from viewport-relative
 * CSS rules. Uses section bounds as the primary signal (immune to vh feedback),
 * falling back to body.scrollHeight (which is reliable once the editor height
 * fix stylesheet has been injected).
 */
function measureContentHeight(sections: SectionInfo[]): number {
  // Primary: compute from section bounds — these reflect actual element positions
  // after the height-fix stylesheet has neutralized viewport-relative min-heights.
  let maxBottom = 0
  for (const s of sections) {
    const bottom = s.bounds.y + s.bounds.height
    if (bottom > maxBottom) maxBottom = bottom
  }
  const sectionHeight = maxBottom > 0 ? Math.ceil(maxBottom) : 0

  // Fallback: body.scrollHeight (reliable now that min-height: 100vh is neutralized)
  const bodyHeight = document.body.scrollHeight

  // Use whichever is larger — sections might miss elements without data-section,
  // but bodyHeight should be accurate with the injected CSS fix.
  return Math.max(sectionHeight, bodyHeight)
}

export function initEditorBridge() {
  if (!isInIframe()) return // Not in iframe

  // Inject height fix CSS before any measurement
  injectEditorHeightFix()

  // Prevent duplicate listener registration on HMR re-execution
  if (!bridgeInitialized) {
    bridgeInitialized = true

    // Monitor navigation within the iframe — notify the portal of page changes
    monitorNavigation()

    // Forward right-click events to the portal so the context menu
    // works when right-clicking inside the iframe (cross-origin boundary
    // prevents the parent from receiving native contextmenu events).
      document.addEventListener('contextmenu', (e: MouseEvent) => {
        e.preventDefault()
        const target = e.target as HTMLElement
        const sectionEl = target.closest?.('[data-section]') as HTMLElement | null
        const formEl = target.closest?.('[data-form-key]') as HTMLElement | null

        // Detect if the right-click target is an image or inside a <picture> element
        const imgEl = target.tagName === 'IMG'
          ? target as HTMLImageElement
          : (target.closest?.('picture')?.querySelector('img') as HTMLImageElement | null)

        // Find ALL images at the click point (handles overlapping images like before/after sliders)
        let allImages: HTMLImageElement[] = []
        if (sectionEl) {
          allImages = findAllImagesAtPoint(sectionEl, e.clientX, e.clientY)
        }

        // If direct target detection found an image not in the position-based list, add it
        if (imgEl && !allImages.includes(imgEl)) {
          allImages.push(imgEl)
        }

        // Use the topmost image for backward-compatible single-image fields
        const topImage = allImages.length > 0 ? allImages[allImages.length - 1] : null

        postToParent('dcs:contextmenu', {
          x: e.clientX,
          y: e.clientY,
          sectionId: sectionEl?.dataset.section ?? null,
          sectionLabel: sectionEl?.dataset.sectionLabel ?? null,
          formId: formEl?.dataset.formKey ?? null,
          // Single image context (backward compatible) — topmost image at click point
          imageUrl: topImage?.currentSrc ?? topImage?.src ?? null,
          imageAlt: topImage?.alt ?? null,
          imageWidth: topImage?.naturalWidth ?? null,
          imageHeight: topImage?.naturalHeight ?? null,
          imageKey: topImage ? getManagedImageKey(topImage) : null,
          imageAltKey: topImage ? getManagedImageAltKey(topImage) : null,
          // All images at click point (for overlapping image scenarios like before/after sliders)
          images: allImages.length > 1
            ? allImages.map(img => ({
                imageUrl: img.currentSrc ?? img.src,
                imageAlt: img.alt ?? null,
                imageWidth: img.naturalWidth,
                imageHeight: img.naturalHeight,
                imageKey: getManagedImageKey(img),
                imageAltKey: getManagedImageAltKey(img),
              }))
            : null,
        })
      }, true)

    // Forward click events to the portal so it can dismiss context menus
    // when the user clicks inside the iframe.
    // NOTE: Left-clicking an image does NOT open image management. Users must
    // right-click → "Replace Image" or use the floating image icon (top-right
    // corner on hover). This prevents accidental sheet openings during normal
    // page interaction and slider/gallery dragging.
    document.addEventListener('click', () => {
      postToParent('dcs:click', {})
    }, true)

    // Listen for portal commands. Origin is not validated because
    // the portal origin varies between environments and all inbound
    // messages are type-checked before processing.
    // Origin cannot be pre-validated because the portal URL varies by environment.
    // All inbound messages are type-checked via the dcs: prefix guard.
    globalThis.self.addEventListener('message', handleMessage) // NOSONAR

    // Watch for VitePress / Vite HMR page updates. When the framework
    // replaces DOM content, our event listeners and AI buttons are lost.
    // Re-discover sections after each update so the portal overlay stays in sync.
    if (import.meta.hot) {
      import.meta.hot.on('vite:afterUpdate', () => {
        // Small delay for the framework to finish DOM updates
        setTimeout(rediscoverAndNotify, 300)
      })
    }
  }

  // Report available sections, text keys, and content height
  const sections = discoverSections()
  const textKeys = discoverTextKeys()
  const reviewKeys = discoverReviewKeys()
  const managedFormIds = discoverManagedFormIds()
  const contentHeight = measureContentHeight(sections)
  lastReportedHeight = contentHeight

  // Detect rich content elements for the initial ready report
  const blogEl = document.querySelector<HTMLElement>(BLOG_CONTENT_SELECTOR)
  const eventEl = document.querySelector<HTMLElement>(EVENT_CONTENT_SELECTOR)

  // Detect blog metadata elements for the initial ready report
  const blogTitleEl = document.querySelector<HTMLElement>('[data-blog-title]')
  const blogSummaryEl = document.querySelector<HTMLElement>('[data-blog-summary]')

  postToParent('dcs:ready', {
    sections,
    textKeys,
    contentHeight,
    hasBlogContent: !!blogEl,
    blogContentHtml: blogEl ? blogEl.innerHTML : null,
    hasEventContent: !!eventEl,
    eventContentHtml: eventEl ? eventEl.innerHTML : null,
    blogTitle: blogTitleEl ? blogTitleEl.textContent?.trim() ?? null : null,
    blogSummary: blogSummaryEl ? blogSummaryEl.textContent?.trim() ?? null : null,
    hasManagedForms: managedFormIds.length > 0,
    managedFormIds,
    hasReviews: reviewKeys.length > 0,
    reviewKeys,
  } satisfies EditorReadyPayload)

  // Observe layout changes (lazy images, dynamic content) and re-report bounds
  observeSectionLayout()
}

// NOTE: Auto-initialization is NOT done here. The Vite plugin's virtual module
// (dcsEditorPlugin) handles initialization by importing and calling initEditorBridge().
// Having auto-init as a side effect of the import caused double-initialization
// (two dcs:ready messages on startup).
